import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { XMarkIcon, DocumentTextIcon, CogIcon, EyeIcon, PencilIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { ApiService } from '../services/api';
import html2pdf from 'html2pdf.js';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId: number;
  userId: number;
  fileType?: 'electrical_scheme' | 'technical_doc';
  onProcessingStart?: () => void;
}

const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  applicationId,
  userId,
  fileType,
  onProcessingStart
}) => {
  const [reportContent, setReportContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false); // Режим редактирования
  const reportViewRef = useRef<HTMLDivElement>(null); // ref для блока отчёта
  // Экспорт отчёта в PDF
  const handleExportPDF = () => {
    if (reportViewRef.current) {
      // Опции для html2pdf.js
      const opt = {
        margin:       0.5,
        filename:     'report.pdf',
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true },
        jsPDF:        { unit: 'in', format: 'a4', orientation: 'landscape' }
      };
      html2pdf().set(opt).from(reportViewRef.current).save();
    }
  };

  useEffect(() => {
    if (isOpen && applicationId) {
      loadReport();
    }
  }, [isOpen, applicationId]);

  const loadReport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await ApiService.getApplicationReport(applicationId);
      setReportContent(response.report || '');
    } catch (err) {
      console.error('Error loading report:', err);
      setError('Ошибка при загрузке отчета');
    } finally {
      setIsLoading(false);
    }
  };

  const saveReport = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await ApiService.updateApplicationReport(applicationId, reportContent);
      setSuccessMessage('Отчет успешно сохранен');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving report:', err);
      setError('Ошибка при сохранении отчета');
    } finally {
      setIsSaving(false);
    }
  };

  const startCalculation = async () => {
    if (!reportContent.trim()) {
      setError('Отчет не может быть пустым');
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      await ApiService.processApplicationReport(applicationId, userId);
      onProcessingStart?.();
      onClose();
    } catch (err) {
      console.error('Error starting calculation:', err);
      setError('Ошибка при запуске расчета');
      setIsProcessing(false);
    }
  };

  const getReportTypeTitle = () => {
    switch (fileType) {
      case 'electrical_scheme':
        return 'Отчет по электрической схеме';
      case 'technical_doc':
        return 'Отчет по технической документации';
      default:
        return 'Отчет';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-hidden">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-full max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 flex items-center">
            <DocumentTextIcon className="h-6 w-6 mr-2" />
            {getReportTypeTitle()}
          </h2>
          <div className="flex items-center gap-3">
            {/* Кнопка экспорта в PDF (только в режиме просмотра и если есть отчёт) */}
            {!isEditMode && reportContent && (
              <button
                onClick={handleExportPDF}
                className="px-3 py-1 rounded text-sm font-medium bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center gap-1"
                title="Экспортировать в PDF"
              >
                <DocumentTextIcon className="h-4 w-4 inline" />
                Экспорт в PDF
              </button>
            )}
            {/* Переключение режимов */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setIsEditMode(false)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  !isEditMode 
                    ? 'bg-blue-500 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <EyeIcon className="h-4 w-4 mr-1 inline" />
                Просмотр
              </button>
              <button
                onClick={() => setIsEditMode(true)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  isEditMode 
                    ? 'bg-blue-500 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <PencilIcon className="h-4 w-4 mr-1 inline" />
                Редактирование
              </button>
            </div>
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col p-6 overflow-hidden">
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <SparklesIcon className="h-8 w-8 text-blue-500 animate-spin mr-3" />
              <span className="text-gray-700">Загрузка отчета...</span>
            </div>
          ) : (
            <>
              <div className="flex flex-col h-full overflow-hidden">
                
                <div className="flex-1 overflow-hidden">
                  {isEditMode ? (
                    /* Режим редактирования */
                    <textarea
                      className="w-full h-full p-4 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm leading-relaxed"
                      value={reportContent}
                      onChange={(e) => setReportContent(e.target.value)}
                      placeholder="Отчет будет сгенерирован автоматически..."
                    />
                  ) : (
                    /* Режим просмотра с Markdown */
                    <div className="h-full overflow-y-auto bg-gray-50 border border-gray-300 rounded-lg p-6" ref={reportViewRef}>
                      {reportContent ? (
                        <div className="markdown-content max-w-none">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              h1: ({children}) => <h1 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-gray-200">{children}</h1>,
                              h2: ({children}) => <h2 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">{children}</h2>,
                              h3: ({children}) => <h3 className="text-xl font-semibold text-gray-700 mb-3 mt-6">{children}</h3>,
                              h4: ({children}) => <h4 className="text-lg font-medium text-gray-700 mb-2 mt-4">{children}</h4>,
                              p: ({children}) => <p className="text-gray-700 mb-4 leading-relaxed">{children}</p>,
                              ul: ({children}) => <ul className="list-disc pl-6 mb-4 space-y-1">{children}</ul>,
                              ol: ({children}) => <ol className="list-decimal pl-6 mb-4 space-y-1">{children}</ol>,
                              li: ({children}) => <li className="text-gray-700">{children}</li>,
                              blockquote: ({children}) => <blockquote className="border-l-4 border-blue-500 pl-4 py-2 mb-4 bg-blue-50 text-gray-700 italic">{children}</blockquote>,
                              code: ({children, className}) => {
                                const inline = !className;
                                return inline ? (
                                  <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
                                ) : (
                                  <code className="block bg-gray-100 text-gray-800 p-4 rounded-lg text-sm font-mono overflow-x-auto mb-4">{children}</code>
                                );
                              },
                              pre: ({children}) => <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto mb-4">{children}</pre>,
                              table: ({children}) => (
                                <div className="overflow-x-auto mb-4">
                                  <table className="min-w-full border-collapse border border-gray-300">
                                    {children}
                                  </table>
                                </div>
                              ),
                              thead: ({children}) => <thead className="bg-gray-50">{children}</thead>,
                              tbody: ({children}) => <tbody>{children}</tbody>,
                              tr: ({children}) => <tr className="border-b border-gray-200">{children}</tr>,
                              th: ({children}) => <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900 bg-gray-100">{children}</th>,
                              td: ({children}) => <td className="border border-gray-300 px-4 py-3 text-gray-700">{children}</td>,
                              a: ({children, href}) => <a href={href} className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
                              strong: ({children}) => <strong className="font-semibold text-gray-900">{children}</strong>,
                              em: ({children}) => <em className="italic text-gray-700">{children}</em>,
                            }}
                          >
                            {reportContent}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <div className="text-gray-500 italic text-center py-12">
                          Отчет пуст или еще не сгенерирован
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Messages */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-300 rounded-lg flex-shrink-0">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              {successMessage && (
                <div className="mt-4 p-3 bg-green-50 border border-green-300 rounded-lg flex-shrink-0">
                  <p className="text-green-700 text-sm">{successMessage}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4 mt-6 flex-shrink-0">
                <button
                  onClick={saveReport}
                  disabled={isSaving}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center"
                >
                  {isSaving ? (
                    <>
                      <SparklesIcon className="h-5 w-5 mr-2 animate-spin" />
                      Сохранение...
                    </>
                  ) : (
                    <>
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Сохранить изменения
                    </>
                  )}
                </button>

                <button
                  onClick={startCalculation}
                  disabled={!reportContent.trim() || isProcessing || isSaving}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center"
                >
                  {isProcessing ? (
                    <>
                      <SparklesIcon className="h-5 w-5 mr-2 animate-spin" />
                      Запуск подбора...
                    </>
                  ) : (
                    <>
                      <CogIcon className="h-5 w-5 mr-2" />
                      Запустить подбор оборудования
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
